import jsPDF from "jspdf";
import { NanumGothicBase64 } from "./fonts/NanumGothic";

/**
 * 숫자를 한글 금액으로 변환
 */
const numberToKorean = (num: number): string => {
  const units = ["", "만", "억", "조"];
  const smallUnits = ["", "십", "백", "천"];
  const koreanNums = ["", "일", "이", "삼", "사", "오", "육", "칠", "팔", "구"];

  if (num === 0) return "영";

  let result = "";
  let unitIndex = 0;

  while (num > 0) {
    const part = num % 10000;
    if (part > 0) {
      let partStr = "";
      let tempPart = part;
      let smallUnitIndex = 0;

      while (tempPart > 0) {
        const digit = tempPart % 10;
        if (digit > 0) {
          if (smallUnitIndex > 0 && digit === 1) {
            partStr = smallUnits[smallUnitIndex] + partStr;
          } else {
            partStr = koreanNums[digit] + smallUnits[smallUnitIndex] + partStr;
          }
        }
        tempPart = Math.floor(tempPart / 10);
        smallUnitIndex++;
      }
      result = partStr + units[unitIndex] + result;
    }
    num = Math.floor(num / 10000);
    unitIndex++;
  }

  return result;
};

/**
 * 숫자를 천 단위 콤마 포맷
 */
const formatNumber = (num: number): string => {
  return num.toLocaleString("ko-KR");
};

/**
 * 안전하게 문자열 반환
 */
const safeText = (text: string | null | undefined, maxLength?: number): string => {
  if (text === null || text === undefined) return "";
  let result = String(text);
  if (maxLength && result.length > maxLength) {
    result = result.substring(0, maxLength);
  }
  return result;
};

/**
 * 공급자 정보 인터페이스
 */
export interface SupplierInfo {
  businessNumber: string;
  companyName: string;
  representativeName: string;
  address: string;
  businessType: string;
  businessItem: string;
}

/**
 * 거래 내역 항목 인터페이스
 */
export interface TransactionItem {
  date: string;
  inCompany: string;
  outCompany: string;
  itemName: string;
  qty: number;
  unitPrice: number;
  amount: number;
  vatAmount: number;
  remark?: string;
}

/**
 * 거래명세표 PDF 생성 옵션
 */
export interface TransactionPdfOptions {
  clientName: string;
  supplier: SupplierInfo;
  items: TransactionItem[];
  fileName?: string;
}

/**
 * 거래명세표 PDF 생성 및 다운로드
 */
export const generateTransactionPdf = (options: TransactionPdfOptions): void => {
  const { clientName, supplier, items, fileName = "거래명세표" } = options;

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  // 나눔고딕 폰트 등록
  doc.addFileToVFS("NanumGothic.ttf", NanumGothicBase64);
  doc.addFont("NanumGothic.ttf", "NanumGothic", "normal");
  doc.setFont("NanumGothic");

  const pageHeight = 297;
  const marginLeft = 10;
  const contentWidth = 190;

  // 합계 계산
  const totalQty = items.reduce((sum, item) => sum + (item.qty || 0), 0);
  const totalAmount = items.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalVat = items.reduce((sum, item) => sum + (item.vatAmount || 0), 0);
  const grandTotal = totalAmount + totalVat;

  // 금액 자릿수에 따라 컬럼 너비 동적 계산
  const calcColWidths = () => {
    // 기본 너비: NO, 일자, 상차지, 하차지, 품목, 수량, 단가, 금액, 부가세액, 비고
    const baseWidths = [10, 16, 28, 28, 20, 14, 16, 18, 18, 22];
    const maxAmount = Math.max(totalAmount, totalVat, ...items.map(i => i.amount || 0), ...items.map(i => i.vatAmount || 0));

    // 천만 단위 이상이면 칸 확장
    if (maxAmount >= 10000000) {
      const extraWidth = maxAmount >= 100000000 ? 8 : 4; // 억 단위면 8, 천만 단위면 4
      // 금액, 부가세액 칸 확장하고 상차지, 하차지에서 빼기
      baseWidths[7] += extraWidth; // 금액
      baseWidths[8] += extraWidth; // 부가세액
      baseWidths[2] -= extraWidth; // 상차지
      baseWidths[3] -= extraWidth; // 하차지
    }
    return baseWidths;
  };

  let y = 15;
  doc.setLineWidth(0.2);

  // Bold 효과를 위한 헬퍼 함수 (텍스트를 약간 offset해서 두 번 그림)
  const drawBoldText = (text: string, x: number, yPos: number, options?: { align?: "left" | "center" | "right" }) => {
    doc.text(text, x, yPos, options);
    doc.text(text, x + 0.2, yPos, options);
  };

  // ==================== 1행: (빈칸)권 | (빈칸)호 | 거래명세표 (전체 테두리) ====================
  const row1H = 8;

  // 전체 외곽 테두리
  doc.rect(marginLeft, y, contentWidth, row1H);

  // 구조: [빈칸 + 권] | [빈칸 + 호] | 거래명세표
  const col1W = 25;  // 빈칸 + 권
  const col2W = 25;  // 빈칸 + 호
  const col3W = contentWidth - col1W - col2W;  // 거래명세표

  doc.setFontSize(9);
  // 첫번째 칸: 빈칸 + "권" (권은 오른쪽에)
  doc.text("권", marginLeft + col1W - 3, y + 5.5, { align: "right" });
  doc.line(marginLeft + col1W, y, marginLeft + col1W, y + row1H);

  // 두번째 칸: 빈칸 + "호" (호는 오른쪽에)
  doc.text("호", marginLeft + col1W + col2W - 3, y + 5.5, { align: "right" });
  doc.line(marginLeft + col1W + col2W, y, marginLeft + col1W + col2W, y + row1H);

  // 거래명세표 (Bold)
  doc.setFontSize(18);
  drawBoldText("거 래 명 세 표", marginLeft + col1W + col2W + col3W / 2, y + 6, { align: "center" });

  y += row1H;

  // ==================== 2행~5행: 좌측(귀하, 테두리없음) + 우측(공급자 테이블) ====================
  const leftW = 85;  // 좌측 귀하 영역 (테두리 없음)
  const rightW = contentWidth - leftW;  // 우측 공급자 테이블
  const rightX = marginLeft + leftW;
  const rowH = 7;
  const supplierH = rowH * 5;  // 공급자 라벨 1행 + 정보 4행

  // 우측 공급자 테이블 외곽선만 그림 (좌측은 테두리 없음)
  doc.rect(rightX, y, rightW, supplierH);

  // ----- 공급자 라벨 행 -----
  doc.setFontSize(9);
  doc.text("공 급 자", rightX + rightW / 2, y + 5, { align: "center" });
  doc.line(rightX, y + rowH, rightX + rightW, y + rowH);

  // 공급자 정보 영역
  const infoY = y + rowH;
  const labelW = 18;
  const valueW1 = 45;
  const labelW2 = 14;

  // 세로선: 라벨 | 값
  doc.line(rightX + labelW, infoY, rightX + labelW, y + supplierH);

  doc.setFontSize(8);

  // ----- 등록번호 행 -----
  doc.text("등록번호", rightX + labelW / 2, infoY + 5, { align: "center" });
  // 등록번호 값: Bold + 중앙정렬
  drawBoldText(safeText(supplier.businessNumber), rightX + labelW + (rightW - labelW) / 2, infoY + 5, { align: "center" });
  doc.line(rightX, infoY + rowH, rightX + rightW, infoY + rowH);

  // ----- 상호/성명 행 -----
  const row2Y = infoY + rowH;
  doc.text("상    호", rightX + labelW / 2, row2Y + 5, { align: "center" });
  doc.text(safeText(supplier.companyName), rightX + labelW + 3, row2Y + 5);
  // 성명 세로선
  doc.line(rightX + labelW + valueW1, row2Y, rightX + labelW + valueW1, row2Y + rowH);
  doc.text("성  명", rightX + labelW + valueW1 + labelW2 / 2, row2Y + 5, { align: "center" });
  doc.line(rightX + labelW + valueW1 + labelW2, row2Y, rightX + labelW + valueW1 + labelW2, row2Y + rowH);
  // 성명 값: 중앙정렬
  const nameValueW = rightW - labelW - valueW1 - labelW2;
  doc.text(safeText(supplier.representativeName), rightX + labelW + valueW1 + labelW2 + nameValueW / 2, row2Y + 5, { align: "center" });
  doc.line(rightX, row2Y + rowH, rightX + rightW, row2Y + rowH);

  // ----- 주소 행 -----
  const row3Y = infoY + rowH * 2;
  doc.text("주    소", rightX + labelW / 2, row3Y + 5, { align: "center" });
  doc.text(safeText(supplier.address, 30), rightX + labelW + 3, row3Y + 5);
  doc.line(rightX, row3Y + rowH, rightX + rightW, row3Y + rowH);

  // ----- 업태/종목 행 -----
  const row4Y = infoY + rowH * 3;
  doc.text("업    태", rightX + labelW / 2, row4Y + 5, { align: "center" });
  // 업태 값: 중앙정렬
  doc.text(safeText(supplier.businessType), rightX + labelW + valueW1 / 2, row4Y + 5, { align: "center" });
  // 종목 세로선
  doc.line(rightX + labelW + valueW1, row4Y, rightX + labelW + valueW1, row4Y + rowH);
  doc.text("종  목", rightX + labelW + valueW1 + labelW2 / 2, row4Y + 5, { align: "center" });
  doc.line(rightX + labelW + valueW1 + labelW2, row4Y, rightX + labelW + valueW1 + labelW2, row4Y + rowH);
  doc.text(safeText(supplier.businessItem, 10), rightX + labelW + valueW1 + labelW2 + 3, row4Y + 5);

  // ===== 좌측: 귀하 영역 (좌측 세로선만 있음) =====
  // 좌측 세로선
  doc.line(marginLeft, y, marginLeft, y + supplierH);

  doc.setFontSize(14);
  // 귀하 오른쪽 정렬 (rightX - 5 위치에 오른쪽 정렬) + Bold
  drawBoldText(safeText(clientName) + " 귀하", rightX - 5, y + 18, { align: "right" });
  doc.setLineWidth(0.4);
  doc.line(marginLeft + 5, y + 21, rightX - 5, y + 21);
  doc.setLineWidth(0.2);

  doc.setFontSize(11);
  doc.text("아래와 같이 계산합니다.", marginLeft + 5, y + 32);

  y += supplierH;

  // ==================== 합계금액 행 ====================
  const totalRowH = 8;
  doc.rect(marginLeft, y, contentWidth, totalRowH);

  doc.setFontSize(11);
  const koreanAmt = numberToKorean(grandTotal);
  // 합계금액 전체 Bold - 왼쪽 정렬 + 패딩
  drawBoldText("합계금액", marginLeft + 5, y + 5.5);
  drawBoldText(koreanAmt + " 원정  (", marginLeft + 45, y + 5.5);
  drawBoldText("₩" + formatNumber(grandTotal) + "  )", marginLeft + contentWidth - 10, y + 5.5, { align: "right" });

  y += totalRowH;

  // ==================== 데이터 테이블 헤더 (2행 구조: 업체 그룹) ====================
  // 컬럼 너비: NO, 일자, 상차지, 하차지, 품목, 수량, 단가, 금액, 부가세액, 비고
  const colWidths = calcColWidths();
  const headerRowH = 5;
  const headerTotalH = headerRowH * 2;

  // 전체 헤더 영역 외곽선
  doc.rect(marginLeft, y, contentWidth, headerTotalH);

  doc.setFontSize(9);
  let colX = marginLeft;

  // NO (2행 병합)
  doc.text("NO", colX + colWidths[0] / 2, y + headerTotalH / 2 + 1.5, { align: "center" });
  doc.line(colX + colWidths[0], y, colX + colWidths[0], y + headerTotalH);
  colX += colWidths[0];

  // 일자 (2행 병합)
  doc.text("일자", colX + colWidths[1] / 2, y + headerTotalH / 2 + 1.5, { align: "center" });
  doc.line(colX + colWidths[1], y, colX + colWidths[1], y + headerTotalH);
  colX += colWidths[1];

  // 업체 (상차지+하차지 병합) - 전체 2행 병합
  const companyW = colWidths[2] + colWidths[3];
  doc.text("업 체", colX + companyW / 2, y + headerTotalH / 2 + 1.5, { align: "center" });
  doc.line(colX + companyW, y, colX + companyW, y + headerTotalH);
  colX += companyW;

  // 품목 (2행 병합)
  doc.text("품목", colX + colWidths[4] / 2, y + headerTotalH / 2 + 1.5, { align: "center" });
  doc.line(colX + colWidths[4], y, colX + colWidths[4], y + headerTotalH);
  colX += colWidths[4];

  // 수량 (2행 병합)
  doc.text("수량", colX + colWidths[5] / 2, y + headerTotalH / 2 + 1.5, { align: "center" });
  doc.line(colX + colWidths[5], y, colX + colWidths[5], y + headerTotalH);
  colX += colWidths[5];

  // 단가 (2행 병합)
  doc.text("단가", colX + colWidths[6] / 2, y + headerTotalH / 2 + 1.5, { align: "center" });
  doc.line(colX + colWidths[6], y, colX + colWidths[6], y + headerTotalH);
  colX += colWidths[6];

  // 금액 (2행 병합)
  doc.text("금액", colX + colWidths[7] / 2, y + headerTotalH / 2 + 1.5, { align: "center" });
  doc.line(colX + colWidths[7], y, colX + colWidths[7], y + headerTotalH);
  colX += colWidths[7];

  // 부가세액 (2행 병합)
  doc.text("부가세액", colX + colWidths[8] / 2, y + headerTotalH / 2 + 1.5, { align: "center" });
  doc.line(colX + colWidths[8], y, colX + colWidths[8], y + headerTotalH);
  colX += colWidths[8];

  // 비고 (2행 병합)
  doc.text("비고", colX + colWidths[9] / 2, y + headerTotalH / 2 + 1.5, { align: "center" });

  y += headerTotalH;

  // ==================== 데이터 행 ====================
  doc.setFontSize(8);
  const dataRowH = 7;

  for (let idx = 0; idx < items.length; idx++) {
    const item = items[idx];

    if (y + dataRowH > pageHeight - 25) {
      doc.addPage();
      doc.setFont("NanumGothic");
      y = 15;
    }

    doc.rect(marginLeft, y, contentWidth, dataRowH);

    let cellX = marginLeft;

    // NO
    doc.text(String(idx + 1), cellX + colWidths[0] / 2, y + 5, { align: "center" });
    doc.line(cellX + colWidths[0], y, cellX + colWidths[0], y + dataRowH);
    cellX += colWidths[0];

    // 일자
    doc.text(safeText(item.date), cellX + colWidths[1] / 2, y + 5, { align: "center" });
    doc.line(cellX + colWidths[1], y, cellX + colWidths[1], y + dataRowH);
    cellX += colWidths[1];

    // 상차지
    doc.text(safeText(item.inCompany, 8), cellX + 2, y + 5);
    doc.line(cellX + colWidths[2], y, cellX + colWidths[2], y + dataRowH);
    cellX += colWidths[2];

    // 하차지
    doc.text(safeText(item.outCompany, 8), cellX + 2, y + 5);
    doc.line(cellX + colWidths[3], y, cellX + colWidths[3], y + dataRowH);
    cellX += colWidths[3];

    // 품목
    doc.text(safeText(item.itemName, 6), cellX + 2, y + 5);
    doc.line(cellX + colWidths[4], y, cellX + colWidths[4], y + dataRowH);
    cellX += colWidths[4];

    // 수량
    doc.text((item.qty || 0).toFixed(2), cellX + colWidths[5] - 2, y + 5, { align: "right" });
    doc.line(cellX + colWidths[5], y, cellX + colWidths[5], y + dataRowH);
    cellX += colWidths[5];

    // 단가
    doc.text(formatNumber(item.unitPrice || 0), cellX + colWidths[6] - 2, y + 5, { align: "right" });
    doc.line(cellX + colWidths[6], y, cellX + colWidths[6], y + dataRowH);
    cellX += colWidths[6];

    // 금액
    doc.text(formatNumber(item.amount || 0), cellX + colWidths[7] - 2, y + 5, { align: "right" });
    doc.line(cellX + colWidths[7], y, cellX + colWidths[7], y + dataRowH);
    cellX += colWidths[7];

    // 부가세액
    doc.text(formatNumber(item.vatAmount || 0), cellX + colWidths[8] - 2, y + 5, { align: "right" });
    doc.line(cellX + colWidths[8], y, cellX + colWidths[8], y + dataRowH);
    cellX += colWidths[8];

    // 비고
    doc.text(safeText(item.remark || "", 8), cellX + 2, y + 5);

    y += dataRowH;
  }

  // 빈 행 채우기
  const minRows = 20;
  const emptyRows = Math.max(0, minRows - items.length);
  for (let i = 0; i < emptyRows; i++) {
    if (y + dataRowH > pageHeight - 25) break;

    doc.rect(marginLeft, y, contentWidth, dataRowH);
    let cellX = marginLeft;
    for (let j = 0; j < colWidths.length - 1; j++) {
      cellX += colWidths[j];
      doc.line(cellX, y, cellX, y + dataRowH);
    }
    y += dataRowH;
  }

  // ==================== 합계 행 ====================
  const sumRowH = 8;
  doc.rect(marginLeft, y, contentWidth, sumRowH);

  doc.setFontSize(9);
  // 합계 라벨: NO + 일자 + 상차지 + 하차지 + 품목
  const sumLabelW = colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4];
  doc.text("합 계", marginLeft + sumLabelW / 2, y + 5.5, { align: "center" });

  let sumX = marginLeft + sumLabelW;
  doc.line(sumX, y, sumX, y + sumRowH);

  // 수량 합계
  doc.text(totalQty.toFixed(2), sumX + colWidths[5] - 2, y + 5.5, { align: "right" });
  sumX += colWidths[5];
  doc.line(sumX, y, sumX, y + sumRowH);

  // 단가 (빈칸)
  sumX += colWidths[6];
  doc.line(sumX, y, sumX, y + sumRowH);

  // 금액 합계
  doc.text(formatNumber(totalAmount), sumX + colWidths[7] - 2, y + 5.5, { align: "right" });
  sumX += colWidths[7];
  doc.line(sumX, y, sumX, y + sumRowH);

  // 부가세 합계
  doc.text(formatNumber(totalVat), sumX + colWidths[8] - 2, y + 5.5, { align: "right" });
  sumX += colWidths[8];
  doc.line(sumX, y, sumX, y + sumRowH);

  // 비고 (빈칸) - 세로선으로 구분됨

  // 파일 다운로드
  const today = new Date();
  const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}`;
  doc.save(`${fileName}_${dateStr}.pdf`);
};

/**
 * 거래명세표 PDF Blob 생성 (미리보기/인쇄용)
 */
export const createTransactionPdfBlob = (options: TransactionPdfOptions): Blob => {
  const { clientName, supplier, items } = options;

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  // 나눔고딕 폰트 등록
  doc.addFileToVFS("NanumGothic.ttf", NanumGothicBase64);
  doc.addFont("NanumGothic.ttf", "NanumGothic", "normal");
  doc.setFont("NanumGothic");

  const pageHeight = 297;
  const marginLeft = 10;
  const contentWidth = 190;

  // 합계 계산
  const totalQty = items.reduce((sum, item) => sum + (item.qty || 0), 0);
  const totalAmount = items.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalVat = items.reduce((sum, item) => sum + (item.vatAmount || 0), 0);
  const grandTotal = totalAmount + totalVat;

  // 금액 자릿수에 따라 컬럼 너비 동적 계산
  const calcColWidths = () => {
    const baseWidths = [10, 16, 28, 28, 20, 14, 16, 18, 18, 22];
    const maxAmount = Math.max(totalAmount, totalVat, ...items.map(i => i.amount || 0), ...items.map(i => i.vatAmount || 0));
    if (maxAmount >= 10000000) {
      const extraWidth = maxAmount >= 100000000 ? 8 : 4;
      baseWidths[7] += extraWidth;
      baseWidths[8] += extraWidth;
      baseWidths[2] -= extraWidth;
      baseWidths[3] -= extraWidth;
    }
    return baseWidths;
  };

  let y = 15;
  doc.setLineWidth(0.2);

  const drawBoldText = (text: string, x: number, yPos: number, options?: { align?: "left" | "center" | "right" }) => {
    doc.text(text, x, yPos, options);
    doc.text(text, x + 0.2, yPos, options);
  };

  // 1행: 권 | 호 | 거래명세표
  const row1H = 8;
  doc.rect(marginLeft, y, contentWidth, row1H);
  const col1W = 25;
  const col2W = 25;
  const col3W = contentWidth - col1W - col2W;
  doc.setFontSize(9);
  doc.text("권", marginLeft + col1W - 3, y + 5.5, { align: "right" });
  doc.line(marginLeft + col1W, y, marginLeft + col1W, y + row1H);
  doc.text("호", marginLeft + col1W + col2W - 3, y + 5.5, { align: "right" });
  doc.line(marginLeft + col1W + col2W, y, marginLeft + col1W + col2W, y + row1H);
  doc.setFontSize(18);
  drawBoldText("거 래 명 세 표", marginLeft + col1W + col2W + col3W / 2, y + 6, { align: "center" });
  y += row1H;

  // 2~5행: 귀하 + 공급자
  const leftW = 85;
  const rightW = contentWidth - leftW;
  const rightX = marginLeft + leftW;
  const rowH = 7;
  const supplierH = rowH * 5;
  doc.rect(rightX, y, rightW, supplierH);
  doc.setFontSize(9);
  doc.text("공 급 자", rightX + rightW / 2, y + 5, { align: "center" });
  doc.line(rightX, y + rowH, rightX + rightW, y + rowH);

  const infoY = y + rowH;
  const labelW = 18;
  const valueW1 = 45;
  const labelW2 = 14;
  doc.line(rightX + labelW, infoY, rightX + labelW, y + supplierH);
  doc.setFontSize(8);

  doc.text("등록번호", rightX + labelW / 2, infoY + 5, { align: "center" });
  drawBoldText(safeText(supplier.businessNumber), rightX + labelW + (rightW - labelW) / 2, infoY + 5, { align: "center" });
  doc.line(rightX, infoY + rowH, rightX + rightW, infoY + rowH);

  const row2Y = infoY + rowH;
  doc.text("상    호", rightX + labelW / 2, row2Y + 5, { align: "center" });
  doc.text(safeText(supplier.companyName), rightX + labelW + 3, row2Y + 5);
  doc.line(rightX + labelW + valueW1, row2Y, rightX + labelW + valueW1, row2Y + rowH);
  doc.text("성  명", rightX + labelW + valueW1 + labelW2 / 2, row2Y + 5, { align: "center" });
  doc.line(rightX + labelW + valueW1 + labelW2, row2Y, rightX + labelW + valueW1 + labelW2, row2Y + rowH);
  const nameValueW = rightW - labelW - valueW1 - labelW2;
  doc.text(safeText(supplier.representativeName), rightX + labelW + valueW1 + labelW2 + nameValueW / 2, row2Y + 5, { align: "center" });
  doc.line(rightX, row2Y + rowH, rightX + rightW, row2Y + rowH);

  const row3Y = infoY + rowH * 2;
  doc.text("주    소", rightX + labelW / 2, row3Y + 5, { align: "center" });
  doc.text(safeText(supplier.address, 30), rightX + labelW + 3, row3Y + 5);
  doc.line(rightX, row3Y + rowH, rightX + rightW, row3Y + rowH);

  const row4Y = infoY + rowH * 3;
  doc.text("업    태", rightX + labelW / 2, row4Y + 5, { align: "center" });
  doc.text(safeText(supplier.businessType), rightX + labelW + valueW1 / 2, row4Y + 5, { align: "center" });
  doc.line(rightX + labelW + valueW1, row4Y, rightX + labelW + valueW1, row4Y + rowH);
  doc.text("종  목", rightX + labelW + valueW1 + labelW2 / 2, row4Y + 5, { align: "center" });
  doc.line(rightX + labelW + valueW1 + labelW2, row4Y, rightX + labelW + valueW1 + labelW2, row4Y + rowH);
  doc.text(safeText(supplier.businessItem, 10), rightX + labelW + valueW1 + labelW2 + 3, row4Y + 5);

  doc.line(marginLeft, y, marginLeft, y + supplierH);
  doc.setFontSize(14);
  drawBoldText(safeText(clientName) + " 귀하", rightX - 5, y + 18, { align: "right" });
  doc.setLineWidth(0.4);
  doc.line(marginLeft + 5, y + 21, rightX - 5, y + 21);
  doc.setLineWidth(0.2);
  doc.setFontSize(11);
  doc.text("아래와 같이 계산합니다.", marginLeft + 5, y + 32);
  y += supplierH;

  // 합계금액 행
  const totalRowH = 8;
  doc.rect(marginLeft, y, contentWidth, totalRowH);
  doc.setFontSize(11);
  const koreanAmt = numberToKorean(grandTotal);
  drawBoldText("합계금액", marginLeft + 5, y + 5.5);
  drawBoldText(koreanAmt + " 원정  (", marginLeft + 45, y + 5.5);
  drawBoldText("₩" + formatNumber(grandTotal) + "  )", marginLeft + contentWidth - 10, y + 5.5, { align: "right" });
  y += totalRowH;

  // 테이블 헤더
  const colWidths = calcColWidths();
  const headerRowH = 5;
  const headerTotalH = headerRowH * 2;
  doc.rect(marginLeft, y, contentWidth, headerTotalH);
  doc.setFontSize(9);
  let colX = marginLeft;

  doc.text("NO", colX + colWidths[0] / 2, y + headerTotalH / 2 + 1.5, { align: "center" });
  doc.line(colX + colWidths[0], y, colX + colWidths[0], y + headerTotalH);
  colX += colWidths[0];

  doc.text("일자", colX + colWidths[1] / 2, y + headerTotalH / 2 + 1.5, { align: "center" });
  doc.line(colX + colWidths[1], y, colX + colWidths[1], y + headerTotalH);
  colX += colWidths[1];

  const companyW = colWidths[2] + colWidths[3];
  doc.text("업 체", colX + companyW / 2, y + headerTotalH / 2 + 1.5, { align: "center" });
  doc.line(colX + companyW, y, colX + companyW, y + headerTotalH);
  colX += companyW;

  doc.text("품목", colX + colWidths[4] / 2, y + headerTotalH / 2 + 1.5, { align: "center" });
  doc.line(colX + colWidths[4], y, colX + colWidths[4], y + headerTotalH);
  colX += colWidths[4];

  doc.text("수량", colX + colWidths[5] / 2, y + headerTotalH / 2 + 1.5, { align: "center" });
  doc.line(colX + colWidths[5], y, colX + colWidths[5], y + headerTotalH);
  colX += colWidths[5];

  doc.text("단가", colX + colWidths[6] / 2, y + headerTotalH / 2 + 1.5, { align: "center" });
  doc.line(colX + colWidths[6], y, colX + colWidths[6], y + headerTotalH);
  colX += colWidths[6];

  doc.text("금액", colX + colWidths[7] / 2, y + headerTotalH / 2 + 1.5, { align: "center" });
  doc.line(colX + colWidths[7], y, colX + colWidths[7], y + headerTotalH);
  colX += colWidths[7];

  doc.text("부가세액", colX + colWidths[8] / 2, y + headerTotalH / 2 + 1.5, { align: "center" });
  doc.line(colX + colWidths[8], y, colX + colWidths[8], y + headerTotalH);
  colX += colWidths[8];

  doc.text("비고", colX + colWidths[9] / 2, y + headerTotalH / 2 + 1.5, { align: "center" });
  y += headerTotalH;

  // 데이터 행
  doc.setFontSize(8);
  const dataRowH = 7;

  for (let idx = 0; idx < items.length; idx++) {
    const item = items[idx];
    if (y + dataRowH > pageHeight - 25) {
      doc.addPage();
      doc.setFont("NanumGothic");
      y = 15;
    }
    doc.rect(marginLeft, y, contentWidth, dataRowH);
    let cellX = marginLeft;

    doc.text(String(idx + 1), cellX + colWidths[0] / 2, y + 5, { align: "center" });
    doc.line(cellX + colWidths[0], y, cellX + colWidths[0], y + dataRowH);
    cellX += colWidths[0];

    doc.text(safeText(item.date), cellX + colWidths[1] / 2, y + 5, { align: "center" });
    doc.line(cellX + colWidths[1], y, cellX + colWidths[1], y + dataRowH);
    cellX += colWidths[1];

    doc.text(safeText(item.inCompany, 8), cellX + 2, y + 5);
    doc.line(cellX + colWidths[2], y, cellX + colWidths[2], y + dataRowH);
    cellX += colWidths[2];

    doc.text(safeText(item.outCompany, 8), cellX + 2, y + 5);
    doc.line(cellX + colWidths[3], y, cellX + colWidths[3], y + dataRowH);
    cellX += colWidths[3];

    doc.text(safeText(item.itemName, 6), cellX + 2, y + 5);
    doc.line(cellX + colWidths[4], y, cellX + colWidths[4], y + dataRowH);
    cellX += colWidths[4];

    doc.text((item.qty || 0).toFixed(2), cellX + colWidths[5] - 2, y + 5, { align: "right" });
    doc.line(cellX + colWidths[5], y, cellX + colWidths[5], y + dataRowH);
    cellX += colWidths[5];

    doc.text(formatNumber(item.unitPrice || 0), cellX + colWidths[6] - 2, y + 5, { align: "right" });
    doc.line(cellX + colWidths[6], y, cellX + colWidths[6], y + dataRowH);
    cellX += colWidths[6];

    doc.text(formatNumber(item.amount || 0), cellX + colWidths[7] - 2, y + 5, { align: "right" });
    doc.line(cellX + colWidths[7], y, cellX + colWidths[7], y + dataRowH);
    cellX += colWidths[7];

    doc.text(formatNumber(item.vatAmount || 0), cellX + colWidths[8] - 2, y + 5, { align: "right" });
    doc.line(cellX + colWidths[8], y, cellX + colWidths[8], y + dataRowH);
    cellX += colWidths[8];

    doc.text(safeText(item.remark || "", 8), cellX + 2, y + 5);
    y += dataRowH;
  }

  // 빈 행
  const minRows = 20;
  const emptyRows = Math.max(0, minRows - items.length);
  for (let i = 0; i < emptyRows; i++) {
    if (y + dataRowH > pageHeight - 25) break;
    doc.rect(marginLeft, y, contentWidth, dataRowH);
    let cellX = marginLeft;
    for (let j = 0; j < colWidths.length - 1; j++) {
      cellX += colWidths[j];
      doc.line(cellX, y, cellX, y + dataRowH);
    }
    y += dataRowH;
  }

  // 합계 행
  const sumRowH = 8;
  doc.rect(marginLeft, y, contentWidth, sumRowH);
  doc.setFontSize(9);
  const sumLabelW = colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4];
  doc.text("합 계", marginLeft + sumLabelW / 2, y + 5.5, { align: "center" });

  let sumX = marginLeft + sumLabelW;
  doc.line(sumX, y, sumX, y + sumRowH);
  doc.text(totalQty.toFixed(2), sumX + colWidths[5] - 2, y + 5.5, { align: "right" });
  sumX += colWidths[5];
  doc.line(sumX, y, sumX, y + sumRowH);
  sumX += colWidths[6];
  doc.line(sumX, y, sumX, y + sumRowH);
  doc.text(formatNumber(totalAmount), sumX + colWidths[7] - 2, y + 5.5, { align: "right" });
  sumX += colWidths[7];
  doc.line(sumX, y, sumX, y + sumRowH);
  doc.text(formatNumber(totalVat), sumX + colWidths[8] - 2, y + 5.5, { align: "right" });
  sumX += colWidths[8];
  doc.line(sumX, y, sumX, y + sumRowH);

  return doc.output("blob");
};
